<template>
  <div class="vd-page-form-component" :class="{ preview: preview }" data-cy="page-form">
    <component
      :is="pageComponentType"
      :title="title"
      :subtitle="subtitle"
      with-background
      :show-back-arrow="(readOnly || !dialog) && !!backLink"
      :back-link="((readOnly || !dialog) && backLink) || null"
      :loading="loading"
      @close="onCancel"
      @back="onBack"
    >
      <template #subtitle>
        <slot name="title-extension"></slot>
      </template>

      <template #actions>
        <slot name="actions" />
      </template>

      <div class="form-body">
        <VdForm
          :rules="rules"
          :form="form"
          :readonly="readOnly"
          :loading="saveLoading"
          :before-cancel="beforeCancel"
          @submit="saveCallback"
          @cancel="onCancel"
        >
          <slot />
        </VdForm>

        <div v-if="readOnly && !hideActions" class="controls">
          <el-button
            v-if="allowDelete"
            type="danger"
            plain
            :delete-loading="deleteLoading"
            class="delete-button"
            data-cy="preview-delete-button"
            @click="onDelete"
          >
            Delete
          </el-button>
          <el-button type="primary" data-cy="preview-edit-button" @click="onEdit">Edit</el-button>
        </div>
      </div>
    </component>
  </div>
</template>

<script lang="ts">
import { VdForm, VdPage } from "@voodoo.io/voodoo-ui";
import { computed, defineComponent, ref, watch, PropType } from "vue";
import { RouteLocationRaw, useRouter } from "vue-router";

import useMessages from "@/hooks/messages";

/**
 * This structural component represents a form into a full page.
 * It display the form along a title (and subtitle) in a beautify way.
 * It has a preview mode which display the form value in a read-only way. In this preview mode,
 * the delete button is displayed if needed.
 */
export default defineComponent({
  name: "VDPageForm",

  components: {
    VdForm,
    VdPage,
  },

  props: {
    /**
     * The title of the page.
     */
    title: {
      type: String,
      default: "",
    },

    /**
     * The subtitle of the page.
     */
    subtitle: {
      type: String,
      default: "",
    },

    /**
     * If true, display the form value in a read-only way
     * and display the delete button if needed
     */
    preview: {
      type: Boolean,
      default: false,
    },

    /**
     * The route location of the previous screen.
     * This is used when clicking on the cancel button in a non preview mode,
     * or by clicking on the left arrow on the preview mode.
     */
    backLink: {
      type: Object as PropType<RouteLocationRaw>,
      default: () => null,
    },

    /**
     * If true, display a confirm message on cancellation if the form has changed.
     */
    displayCancelAlert: {
      type: Boolean,
      default: false,
    },

    /**
     * A boolean that reflect if the form has changed.
     * Hint: use the hook "form-tracking" to get this value.
     */
    formChanged: {
      type: Boolean,
      default: false,
    },

    /**
     * If true, the form page is displayed as dialog
     */
    dialog: {
      type: Boolean,
      default: false,
    },

    /**
     * The route location of the edit screen.
     * This is used when clicking on the edit button in a preview mode.
     */
    editLink: {
      type: Object as PropType<RouteLocationRaw>,
      default: () => null,
    },

    /**
     * The form object which contains the value.
     * Ex: {username: 'bob', password: '1234'}.
     */
    form: {
      type: Object,
      required: true,
    },

    /**
     * The form validation rules.
     * See https://github.com/yiminghe/async-validator for more details.
     */
    rules: {
      type: Object,
      required: false,
      default: () => null,
    },

    /**
     * A simple function which will be called on submit.
     * You can throw errors in it, VDForm will catch those errors in a beautiful way.
     */
    saveCallback: {
      type: Function as PropType<(payload: Event) => void>,
      required: true,
    },

    /**
     * Loading status of the page.
     */
    loading: Boolean,

    /**
     * If true, a loader will be displayed on the save button.
     */
    saveLoading: Boolean,

    /**
     * If true, a loader will be displayed on the delete button.
     */
    deleteLoading: Boolean,

    /**
     * If true, the delete button will be displayed.
     */
    allowDelete: Boolean,

    /**
     * If true, hide the "delete" and "edit" action buttons
     */
    hideActions: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["delete"],

  /**
   * Events:
   *
   * @delete: called when one clicks on the delete button and confirms the action.
   */

  setup(props, { emit }) {
    const router = useRouter();
    const { confirm } = useMessages();

    const readOnly = ref(props.preview);

    const onEdit = () => {
      if (props.editLink) {
        router.push(props.editLink);
      }
    };

    const pageComponentType = computed(() => (props.dialog ? "vd-page-dialog" : VdPage));

    watch(
      () => props.preview,
      (inPreviewMode) => {
        readOnly.value = inPreviewMode;
      }
    );

    const beforeCancel = async () => {
      if (props.displayCancelAlert && props.formChanged) {
        return confirm("Do you really want to leave this form? All your modification will be lost...", {
          confirmButtonText: "Yes I'm sure",
        });
      }
      return true;
    };

    const onCancel = async () => {
      if (props.backLink) {
        router.push(props.backLink);
      }
    };

    const onDelete = async () => {
      const confirmed = await confirm("Do you really want to delete?");
      if (confirmed) {
        emit("delete");
      }
    };

    const onBack = () => {
      if (props.backLink) {
        router.push(props.backLink);
      }
    };

    return {
      onCancel,
      onDelete,
      onEdit,
      onBack,
      readOnly,
      pageComponentType,
      beforeCancel,
    };
  },
});
</script>

<style lang="scss">
:root {
  --vdpageform-readonly-input-text-color: $soft-black;
}

.vd-page-form-component {
  .form-header {
    display: flex;
    align-items: center;
    margin-bottom: size("L") + size("XS");

    .back-link {
      margin-right: 15px;
      display: flex;
      align-items: center;
    }
  }

  .vd-form {
    .is-disabled {
      .el-input__inner,
      .el-textarea__inner {
        color: var(--vdpageform-readonly-input-text-color);
      }
    }
  }

  .controls {
    display: flex;
    justify-content: flex-end;
  }

  /** overwritten attributes when the form is in a dialog */
  .el-dialog {
    .body {
      margin-top: 0;
      color: var(--vd-base-primary-text-color);
    }

    .form-body {
      padding-top: 0;
    }
  }
}
</style>
